import { type MutableRefObject, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import { parseJsonString } from 'pano-utils';
import { SOCKET_DOMAIN } from '@constants';
import {
  type SocketMessage,
  SocketMessageType,
  SocketTaskMessageType,
} from '@types';
import { queryClient, queryKeys } from '@queryClient';
import { sendError } from '@components/Noti';
import getCookie from '@utils/getCookie';
import { getAppKey } from '@utils/getAppKey';
import {
  $libUploadingMatrix,
  $libUploadingPano,
  $libUploadingPVideo,
} from '../stores/libUploading';
import refetchLibSpace from './refetchLibSpace';

const refetchItems = (queryKey: string) => {
  queryClient.refetchQueries([queryKey, 'items'], {
    active: true,
  });
  refetchLibSpace();
};

const messageHandler = (
  message: string,
  idsRef: MutableRefObject<number[]>
) => {
  const msg = parseJsonString(message);
  const { type: messageType, content } = msg as SocketMessage;
  if (messageType !== SocketMessageType.task || !content) return;
  if (!content.success) {
    sendError(
      content.message
        ? content.message
        : `${content.name}图片数据错误，请重新处理后再上传`
    );
    return;
  }
  const { type, id } = content;
  if (type === SocketTaskMessageType.pano) {
    refetchItems(queryKeys.libListPano);
    $libUploadingPano.api.removeByLibId(id);
  } else if (type === SocketTaskMessageType.matrix) {
    refetchItems(queryKeys.libListMatrix);
    $libUploadingMatrix.api.removeByLibId(id);
  } else if (type === SocketTaskMessageType.video) {
    if (!idsRef.current.includes(id)) {
      idsRef.current.push(id);
      refetchItems(queryKeys.libListPVideo);
      $libUploadingPVideo.api.removeByLibId(id);
    }
  } else if (type === SocketTaskMessageType.reupload) {
    refetchItems(queryKeys.libListPano);
  } else if (type === SocketTaskMessageType.matrixReupload) {
    refetchItems(queryKeys.libListMatrix);
  }
};

const useSocket = () => {
  const idsRef = useRef([]); // 处理全景视频消息过多
  useEffect(() => {
    const socket = io.connect(SOCKET_DOMAIN, {
      secure: true,
      timeout: 5000,
      transports: ['websocket', 'flashsocket'],
    });
    const connectHandler = () => {
      socket.emit('auth', {
        // @ts-ignore
        socket: socket.io.engine.id,
        authorization: getCookie(),
        appKey: getAppKey(),
      });
    };
    socket.on('connect', connectHandler);
    socket.on('reconnect', connectHandler);
    socket.on('message', (msg: string) => messageHandler(msg, idsRef));
    return () => {
      socket.disconnect();
    };
  }, []);
};

export default useSocket;
